import { ApplicationPathType } from '@modules/test-development/enums/application-path-type.enum';
import { TestPlanLabType } from '@modules/test-development/enums/test-plan-lab-type.enum';
import { Platform } from '@modules/test-development/enums/platform.enum';
import {
  SKIP,
  alias,
  custom,
  list,
  deserialize,
  object,
  optional,
  serializable,
} from 'serializr';
import { ApplicationVersion } from '@models/application-version.model';
import { Base } from '@models/base.model';
import { Capability } from './capability.model';
import { EnvironmentSettings } from './environment-settings.model';
import { PageObject } from '@models/page-object.model';
import { StatusConstant } from '@modules/test-development/enums/status-constant.enum';
import { PlatformOsVersion } from './platform-os-version.model';
import { PlatformBrowserVersion } from '@modules/test-development/models/platform-browser-version.model';
import { PlatformScreenResolution } from '@modules/test-development/models/platform-screen-resolution.model';
import { PlatformDevice } from '@modules/test-development/models/platform-device.model';
import { EnvironmentBackupDevice } from './environment-backup-device.model';

export class TestDevice extends Base implements PageObject {
  @serializable
  public disable: boolean;
  @serializable
  public executionId: number;
  @serializable
  public platformOsVersionId: number; // OsVersion
  @serializable
  public platformBrowserVersionId: number; // BrowserVersion
  @serializable
  public platformScreenResolutionId: number; // ScreenResolution
  @serializable
  public platformDeviceId: number; //  Platform
  @serializable(
    alias(
      'appPathType',
      custom(
        (v) => {
          if (v == ApplicationPathType.USE_PATH) return '1';
          if (v == ApplicationPathType.APP_DETAILS) return '2';
          if (v == ApplicationPathType.UPLOADS) return '3';
        },
        (v) => {
          if (v == '1' || v == 'USE_PATH') return ApplicationPathType.USE_PATH;
          if (v == '2' || v == 'APP_DETAILS')
            return ApplicationPathType.APP_DETAILS;
          if (v == '3' || v == 'UPLOADS') return ApplicationPathType.UPLOADS;
          return v;
        },
      ),
    ),
  )
  public appPathType: ApplicationPathType;
  @serializable
  public appUploadId: number;
  @serializable
  public appUploadVersionId: number;
  @serializable
  public appUrl: string;
  @serializable(optional())
  public status: StatusConstant;
  @serializable
  public browserStackAppId: string;
  @serializable
  public sauceLabAppId: string;
  @serializable
  public androidAppPackage: string;
  @serializable
  public androidAppActivity: string;
  @serializable
  public iosBundleId: number;
  @serializable(object(EnvironmentSettings))
  public settings: EnvironmentSettings;
  @serializable
  public targetMachine: number;
  @serializable
  public isObsolete: boolean;
  @serializable
  public deviceId: number;
  @serializable
  public desktopAppPath: string;
  @serializable
  public isCameraImageInjectionEnabled: boolean;
  @serializable
  public isNetworkLogsEnabled: boolean;
  @serializable
  public isTouchIdEnrolled: boolean;
  @serializable(
    custom(
      (v) => {
        if (v) return v;
        return null;
      },
      (v) => {
        if (v) return v;
        return null;
      },
    ),
  )
  @serializable(
    optional(
      custom(
        (v) => v,
        (v) => v,
      ),
    ),
  )
  public uftOptionalArguments: string;


  @serializable(
    custom(
      (v) => {
        if (v) return v;
        return null;
      },
      (v) => {
        if (v) return v;
        return null;
      },
    ),
  )
  public backupDevices: EnvironmentBackupDevice[];
  @serializable
  public title: string;
  @serializable
  public matchBrowserVersion: boolean;
  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => {
        if (v == null) return false;
        return v;
      },
    ),
  )
  public runTestSuitesInParallel: boolean;
  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => {
        if (v == null) return false;
        return v;
      },
    ),
  )
  public runTestCasesInParallel: boolean;
  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => {
        if (v == null) return false;
        return v;
      },
    ),
  )
  public createSessionAtCaseLevel: boolean;

  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => {
        if (v == null) return false;
        return v;
      },
    ),
  )
  public createSessionAtIterationLevel: boolean = false;

  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => v,
    ),
  )
  public isHeadless: boolean;

  @serializable(
    custom(
      (v) => {
        if (v == null) return false;
        return v;
      },
      (v) => {
        if (v == null) return false;
        return v;
      },
    ),
  )
  public resetSessionAtSuiteLevel: boolean = false;

  @serializable
  public executionLabType: TestPlanLabType;

  @serializable
  public browser: string;

  @serializable(
    custom(
      (v) => {
        if (v == null) return v;
        const capabilities = [];
        v.forEach((item) => capabilities.push(item.serialize()));
        return JSON.stringify(capabilities);
      },
      (v) => {
        if (v == null) return v;
        const capabilities = [];
        if (typeof v == 'string') {
          if (/\\\\"/g.test(v)) {
            v = v.replace(/\\"/g, '"');
            v = v.replace(/\\"/g, '"');
          } else if (v.startsWith('[{\\"')) {
            v = v.replace(/\\"/g, '"');
          }
          v = JSON.parse(v);
          v.forEach((capability) => {
            capabilities.push(new Capability().deserialize(capability));
          });
        } else if (v instanceof Object)
          v.forEach((capability) => {
            capabilities.push(new Capability().deserialize(capability));
          });
        return capabilities;
      },
    ),
  )
  public capabilities: Capability[];
  @serializable
  public applicationVersionId: number;
  @serializable
  public prerequisiteEnvironmentId: number;
  @serializable
  public prerequisiteEnvironmentIdIndex: number;

  @serializable(
    custom(
      (v) => v,
      (v) => {
        if (v && v instanceof Object) return v.filter((res) => res);
      },
    ),
  )
  public suiteIds: number[] = [];

  @serializable(
    custom(
      (v) => v,
      (v) => {
        if (v && v instanceof Object) return v.filter((res) => res);
      },
    ),
  )
  public runTestCasesInSequenceSuiteIds: number[];

  @serializable
  public runInMobileDebug: boolean;

  @serializable
  public runInWebDebug: boolean;

  @serializable
  public pauseOnFailure: boolean;

  @serializable
  public runTillStepNumber: number;

  @serializable
  public testDataSetId: number;

  @serializable
  public isRealDevice: boolean;

  @serializable
  public platform: Platform;
  public version: ApplicationVersion;

  @serializable(optional(object(PlatformOsVersion)))
  public platformOsVersion: PlatformOsVersion;

  @serializable(optional(object(PlatformBrowserVersion)))
  public platformBrowserVersion: PlatformBrowserVersion;

  @serializable(optional(object(PlatformScreenResolution)))
  public platformScreenResolution: PlatformScreenResolution;

  @serializable(optional(object(PlatformDevice)))
  public platformDevice: PlatformDevice;

  @serializable
  public numSuites: number;

  @serializable
  public testSuites: [];

  @serializable
  public isPredefined: boolean;

  // Variables used internally, not sent in API call
  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public prerequisiteTestDeviceName: string;

  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public uniqueId: number;

  get isWindows() {
    return this.platform == Platform.Windows;
  }

  get isMac() {
    return this.platform == Platform.Mac;
  }

  get isIOS() {
    return (
      this.platformOsVersion?.platform == Platform.iOS ||
      this.platformDevice?.platform == Platform.iOS
    );
  }

  get isLinux() {
    return this.platform == Platform.Linux;
  }

  get isAndroid() {
    return (
      this.platformOsVersion?.platform == Platform.Android ||
      this.platformDevice?.platform == Platform.Android
    );
  }

  get isChrome() {
    return (
      this.browser?.toUpperCase() == 'GOOGLECHROME' ||
      this.browser?.toUpperCase() == 'CHROME'
    );
  }

  get isFirefox() {
    return (
      this.browser?.toUpperCase() == 'FIREFOX' ||
      this.browser?.toUpperCase() == 'MOZILLAFIREFOX'
    );
  }

  get isSafari() {
    return this.browser?.toUpperCase() == 'SAFARI';
  }

  get isEdge() {
    return (
      this.browser?.toUpperCase() == 'EDGE' ||
      this.browser?.toUpperCase() == 'MICROSOFTEDGE'
    );
  }

  get isIE() {
    return this.browser?.toUpperCase() == 'INTERNETEXPLORER';
  }

  get isTestsigmaLab() {
    return this.executionLabType === TestPlanLabType.TestsigmaLab;
  }

  get isHybrid() {
    return this.executionLabType === TestPlanLabType.Hybrid;
  }

  get isBrowserStack() {
    return this.executionLabType === TestPlanLabType.BrowserStack;
  }

  get isLambdaTest() {
    return this.executionLabType === TestPlanLabType.LambdaTest;
  }

  get isKobiton() {
    return this.executionLabType === TestPlanLabType.Kobiton;
  }

  get isSauceLabs() {
    return this.executionLabType === TestPlanLabType.SauceLabs;
  }

  get isExternalLabs() {
    return (
      this.executionLabType === TestPlanLabType.SauceLabs ||
      this.executionLabType === TestPlanLabType.BrowserStack
    );
  }

  get isDebugMode() {
    return this.runInMobileDebug || this.runInWebDebug;
  }

  deserialize(input: any): this {
    return Object.assign(this, deserialize(TestDevice, input));
  }
}
